/**
 * Admin API Client
 *
 * Fetches data from the Still Here Admin API endpoints.
 *
 * Architecture:
 * - In production: Uses relative URLs (/api/...) which are proxied by
 *   Cloudflare Pages Functions to api.stillhere.live
 * - The Pages Function forwards the CF-Access-JWT-Assertion header for auth
 * - This same-origin approach avoids CORS issues with credentials
 */

import type {
  StatsOverviewResponse,
  StatsUsersResponse,
  StatsCohortsResponse,
  StatsInvitesResponse,
  StatsInviteByIdResponse,
  StatsRevenueResponse,
  StatsSubscriptionsResponse,
  StatsSmsResponse,
  StatsHealthResponse,
  StatsWorkersResponse,
  StatsWorkersTimeseriesResponse,
} from "../types/admin";

/**
 * API base path
 * - Production: Uses relative /api path (proxied to api.stillhere.live)
 * - Development with VITE_API_URL: Uses direct URL (for local backend testing)
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

/**
 * Generic fetch wrapper with error handling
 */
async function fetchApi<T>(endpoint: string, params?: URLSearchParams): Promise<T> {
  // Build URL - relative for proxy, absolute for direct
  const isRelative = API_BASE_URL.startsWith("/");
  let url: string;
  
  if (isRelative) {
    // Relative URL for Pages Function proxy
    url = `${API_BASE_URL}${endpoint}`;
    if (params && params.toString()) {
      url += `?${params.toString()}`;
    }
  } else {
    // Absolute URL for direct API access (development)
    const fullUrl = new URL(`${API_BASE_URL}${endpoint}`);
    if (params) {
      fullUrl.search = params.toString();
    }
    url = fullUrl.toString();
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    // Include credentials for Cloudflare Access cookie/JWT
    credentials: "include",
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("Access denied. Please ensure you are logged in via Cloudflare Access.");
    }
    if (response.status === 503) {
      throw new Error("Analytics service unavailable. Please try again later.");
    }
    const errorBody = await response.text();
    throw new Error(`API Error (${response.status}): ${errorBody}`);
  }

  return response.json() as Promise<T>;
}

/**
 * Build date range query params
 */
function buildDateRangeParams(startDate?: string, endDate?: string): URLSearchParams {
  const params = new URLSearchParams();
  if (startDate) params.set("start", startDate);
  if (endDate) params.set("end", endDate);
  return params;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * GET /admin/stats/overview
 * Dashboard overview with key metrics
 */
export async function getStatsOverview(
  startDate?: string,
  endDate?: string
): Promise<StatsOverviewResponse> {
  const params = buildDateRangeParams(startDate, endDate);
  return fetchApi<StatsOverviewResponse>("/admin/stats/overview", params);
}

/**
 * GET /admin/stats/users
 * User metrics over time
 */
export async function getStatsUsers(
  startDate?: string,
  endDate?: string
): Promise<StatsUsersResponse> {
  const params = buildDateRangeParams(startDate, endDate);
  return fetchApi<StatsUsersResponse>("/admin/stats/users", params);
}

/**
 * GET /admin/stats/users/cohorts
 * Cohort retention analysis
 */
export async function getStatsCohorts(): Promise<StatsCohortsResponse> {
  return fetchApi<StatsCohortsResponse>("/admin/stats/users/cohorts");
}

/**
 * GET /admin/stats/invites
 * Invite funnel metrics
 */
export async function getStatsInvites(
  startDate?: string,
  endDate?: string
): Promise<StatsInvitesResponse> {
  const params = buildDateRangeParams(startDate, endDate);
  return fetchApi<StatsInvitesResponse>("/admin/stats/invites", params);
}

/**
 * GET /admin/stats/invites/:inviteId
 * Single invite event timeline
 */
export async function getStatsInviteById(inviteId: string): Promise<StatsInviteByIdResponse> {
  return fetchApi<StatsInviteByIdResponse>(`/admin/stats/invites/${encodeURIComponent(inviteId)}`);
}

/**
 * GET /admin/stats/revenue
 * Revenue and MRR metrics
 */
export async function getStatsRevenue(
  startDate?: string,
  endDate?: string
): Promise<StatsRevenueResponse> {
  const params = buildDateRangeParams(startDate, endDate);
  return fetchApi<StatsRevenueResponse>("/admin/stats/revenue", params);
}

/**
 * GET /admin/stats/subscriptions
 * Subscription tier distribution
 */
export async function getStatsSubscriptions(
  startDate?: string,
  endDate?: string
): Promise<StatsSubscriptionsResponse> {
  const params = buildDateRangeParams(startDate, endDate);
  return fetchApi<StatsSubscriptionsResponse>("/admin/stats/subscriptions", params);
}

/**
 * GET /admin/stats/sms
 * SMS delivery statistics
 */
export async function getStatsSms(hours: number = 24): Promise<StatsSmsResponse> {
  const params = new URLSearchParams();
  params.set("hours", hours.toString());
  return fetchApi<StatsSmsResponse>("/admin/stats/sms", params);
}

/**
 * GET /admin/stats/health
 * System health metrics
 */
export async function getStatsHealth(): Promise<StatsHealthResponse> {
  return fetchApi<StatsHealthResponse>("/admin/stats/health");
}

/**
 * GET /admin/stats/workers
 * Cloudflare Worker performance metrics
 */
export async function getStatsWorkers(hours: number = 24): Promise<StatsWorkersResponse> {
  const params = new URLSearchParams();
  params.set("hours", hours.toString());
  return fetchApi<StatsWorkersResponse>("/admin/stats/workers", params);
}

/**
 * GET /admin/stats/workers/timeseries
 * Time-series Cloudflare Worker metrics for charting
 * @param hours - Number of hours back (default: 24)
 * @param resolution - "minute" or "hour" (auto-selected if not provided)
 */
export async function getStatsWorkersTimeseries(
  hours: number = 24,
  resolution?: "minute" | "hour"
): Promise<StatsWorkersTimeseriesResponse> {
  const params = new URLSearchParams();
  params.set("hours", hours.toString());
  if (resolution) {
    params.set("resolution", resolution);
  }
  return fetchApi<StatsWorkersTimeseriesResponse>("/admin/stats/workers/timeseries", params);
}
